import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import PropTypes from "prop-types";

import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";

import { app } from "../firebase/firebase.js";
import { authService } from "../services/authService.js";

const AuthContext = createContext({});

const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        try {
          setLoading(true);

          if (currentUser) {
            const idToken =
              await currentUser.getIdToken();

            setToken(idToken);

            setUser({
              uid: currentUser.uid,
              email: currentUser.email,
              displayName:
                currentUser.displayName || "",
              photoURL:
                currentUser.photoURL || "",
              emailVerified:
                currentUser.emailVerified,
            });
          } else {
            setUser(null);
            setToken(null);
          }
        } catch (error) {
          console.error(
            "Auth State Error:",
            error
          );
        } finally {
          setLoading(false);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  /**
   * REGISTER USER
   */
  const register = async (
    email,
    password,
    fullName
  ) => {
    try {
      console.log(
        "Creating Firebase account..."
      );

      const credential =
        await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );

      await updateProfile(
        credential.user,
        {
          displayName: fullName,
        }
      );

      await sendEmailVerification(
        credential.user
      );

      try {
        await authService.register(fullName);
        console.log(
          "Backend registration successful"
        );
      } catch (backendError) {
        console.warn(
          "Backend registration failed:",
          backendError
        );
      }

      return credential;
    } catch (error) {
      console.error(
        "Firebase Register Error:",
        error
      );

      switch (error.code) {
        case "auth/email-already-in-use":
          throw new Error(
            "Email address is already registered."
          );

        case "auth/invalid-email":
          throw new Error(
            "Invalid email address."
          );

        case "auth/weak-password":
          throw new Error(
            "Password must be at least 6 characters."
          );

        case "auth/network-request-failed":
          throw new Error(
            "Network error. Check your internet connection."
          );

        case "auth/operation-not-allowed":
          throw new Error(
            "Email/password authentication is not enabled in Firebase."
          );

        default:
          throw new Error(
            error.message ||
              "Unable to create account."
          );
      }
    }
  };

  /**
   * LOGIN USER
   */
  const login = async (
    email,
    password
  ) => {
    try {
      const credential =
        await signInWithEmailAndPassword(
          auth,
          email,
          password
        );

      if (
        !credential.user.emailVerified
      ) {
        throw new Error(
          "Please verify your email before logging in."
        );
      }

      return credential;
    } catch (error) {
      console.error(
        "Login Error:",
        error
      );

      switch (error.code) {
        case "auth/user-not-found":
          throw new Error(
            "User not found."
          );

        case "auth/wrong-password":
          throw new Error(
            "Incorrect password."
          );

        case "auth/invalid-credential":
          throw new Error(
            "Invalid email or password."
          );

        default:
          throw error;
      }
    }
  };

  /**
   * GOOGLE LOGIN
   */
  const loginWithGoogle =
    async () => {
      try {
        const credential =
          await signInWithPopup(
            auth,
            googleProvider
          );

        try {
          await authService.register(
            credential.user.displayName || email.split('@')[0]
          );
        } catch (error) {
          console.warn(
            "Backend sync failed:",
            error
          );
        }

        return credential;
      } catch (error) {
        console.error(
          "Google Login Error:",
          error
        );

        throw error;
      }
    };

  /**
   * LOGOUT
   */
  const logout = async () => {
    try {
      await firebaseSignOut(auth);
      authService.logout();
      setUser(null);
      setToken(null);
    } catch (error) {
      console.error(
        "Logout Error:",
        error
      );
    }
  };

  /**
   * FORGOT PASSWORD
   */
  const forgotPassword =
    async (email) => {
      try {
        await sendPasswordResetEmail(
          auth,
          email
        );
      } catch (error) {
        console.error(
          "Password Reset Error:",
          error
        );
        throw error;
      }
    };

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      register,
      login,
      loginWithGoogle,
      logout,
      forgotPassword,
    }),
    [user, token, loading]
  );

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;